import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "creating-programs-and-environments"
    }}>{`Creating Programs and Environments`}</h1>
    <h2 {...{
      "id": "creating-aem-cloud-service-programs"
    }}>{`Creating AEM Cloud Service Programs`}</h2>
    <p>{`Based on the entitlements available for an organization, AEM Cloud Service programs may be created through the Cloud Manager API. This is done by executing a `}<inlineCode parentName="p">{`POST`}</inlineCode>{` request to the `}<inlineCode parentName="p">{`http://ns.adobe.com/adobecloud/rel/programs`}</inlineCode>{` HAL link of the tenant resource. To discover the tenant resource, you can either navigate to the `}<inlineCode parentName="p">{`http://ns.adobe.com/adobecloud/rel/tenant`}</inlineCode>{` HAL link from any existing program `}<em parentName="p">{`or`}</em>{` execute a `}<inlineCode parentName="p">{`GET`}</inlineCode>{` request to `}<inlineCode parentName="p">{`https://cloudmanager.adobe.io/api/tenants`}</inlineCode>{`. This response will contain exactly one tenant resource.`}</p>
    <h3 {...{
      "id": "creating-a-production-program"
    }}>{`Creating a Production Program`}</h3>
    <p>{`To create a production program, the body of the `}<inlineCode parentName="p">{`POST`}</inlineCode>{` request needs to include the type `}<inlineCode parentName="p">{`aem_cloud_service`}</inlineCode>{`, a name, and at least one solution name. The allowed solutions are `}<inlineCode parentName="p">{`aemsites`}</inlineCode>{` and `}<inlineCode parentName="p">{`aemassets`}</inlineCode>{`.`}</p>
    <InlineAlert slots="text" mdxType="InlineAlert" />
    <p>{`Creating AEM Forms programs will be available in a forthcoming release.`}</p>
    <p>{`For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-plaintext"
      }}>{`POST https://cloudmanager.adobe.io/api/tenant/1/programs

{
  "name": "My Second Program",
  "type": "aem_cloud_service",
  "solutionNames": ["aemsites"]
}
`}</code></pre>
    <h3 {...{
      "id": "creating-a-sandbox-program"
    }}>{`Creating a Sandbox Program`}</h3>
    <p>{`To create a sandbox program, the body of the `}<inlineCode parentName="p">{`POST`}</inlineCode>{` request needs to include the type `}<inlineCode parentName="p">{`aem_cloud_service`}</inlineCode>{`, a name, and a capabilities object that specifies that it is a sandbox program.`}</p>
    <p>{`For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-plaintext"
      }}>{`POST https://cloudmanager.adobe.io/api/tenant/1/programs

{
  "name": "My Second Program",
  "type": "aem_cloud_service",
  "capabilities": {
    "sandbox": true
  }
}
`}</code></pre>
    <h2 {...{
      "id": "creating-aem-cloud-service-environments"
    }}>{`Creating AEM Cloud Service Environments`}</h2>
    <p>{`In order to find the set of currently available regions, execute a GET request to the `}<inlineCode parentName="p">{`http://ns.adobe.com/adobecloud/rel/regions`}</inlineCode>{` HAL link of the program.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-plaintext"
      }}>{`GET https://cloudmanager.adobe.io/api/program/4/regions
`}</code></pre>
    <p>{`This will return a list like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-plaintext"
      }}>{`{
  "_embedded": {
    "regions": [
      {
        "name": "va7"
      },
      {
        "name": "nld2"
      }
    ]
  },
  "_totalNumberOfItems": 2
}
`}</code></pre>
    <p>{`The set of available regions will vary based on capacity and entitlements, so always check this list before creating an environment. The superset of possible regions are:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`aus5`}</inlineCode>{` - Australia Southeast`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`can2`}</inlineCode>{` - Canada`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`deu6`}</inlineCode>{` - Germany`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`gbr9`}</inlineCode>{` - UK South`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`jpn4`}</inlineCode>{` - Japan`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`nld2`}</inlineCode>{` - West Europe`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`sgp5`}</inlineCode>{` - Singapore`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`va7`}</inlineCode>{` - East US`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`wa1`}</inlineCode>{` - West US`}</li>
    </ul>
    <h3 {...{
      "id": "creating-a-single-region-environment"
    }}>{`Creating a Single Region Environment`}</h3>
    <p>{`Using the region value, you can then execute a POST request to the `}<inlineCode parentName="p">{`http://ns.adobe.com/adobecloud/rel/environments`}</inlineCode>{` HAL link of the program with the desired name and type.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-plaintext"
      }}>{`POST https://cloudmanager.adobe.io/api/program/4/environments

{
  "name" : "my-second-dev-environment",
  "type" : "dev",
  "region" : "va7"
}
`}</code></pre>
    <h3 {...{
      "id": "creating-a-multi-region-environment"
    }}>{`Creating a Multi Region Environment`}</h3>
    <p>{`To create multi region, the body of the `}<inlineCode parentName="p">{`POST`}</inlineCode>{` request needs to include the `}<inlineCode parentName="p">{`secondaryRegionDeployments`}</inlineCode>{` that specifies the regions in which additional publish instances should be provisioned.
For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-plaintext"
      }}>{`POST https://cloudmanager.adobe.io/api/program/4/environments

{
  "name" : "my-multi-region-prod-environment",
  "type" : "prod",
  "region" : "va7",
  "secondaryRegionDeployments" : [
     {
       "region" : "wa1"
     },
     {
       "region" : "sgp5"
     }
  ]
}
`}</code></pre>
    <h3 {...{
      "id": "important-limitations"
    }}>{`Important Limitations`}</h3>
    <p>{`Usage of this API will be limited by the existing environment limits. Specifically:`}</p>
    <ul>
      <li parentName="ul">{`Sandbox programs are limited to one environment of each type.`}</li>
      <li parentName="ul">{`Production programs are limited to one stage and one production environment.`}</li>
      <li parentName="ul">{`The total number of dev environments in production programs is limited to the number of dev environments contracted for that organization.`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      